import { Link } from "react-router-dom";
import { COMPANY } from "../data/company";
import SEO from "../components/SEO";


export default function About() {
  return (
    <>
      <SEO
        title="Om Andreas"
        description={`Læs om ${COMPANY.name} i ${COMPANY.city}. Tømrerarbejde udført med personligt engagement, faglig præcision og solidt håndværk. Kontakt ${COMPANY.phone}.`}
        path="/om-andreas"
        image="/_assets/toemrer-vaerksted-detalje.jpg"
      />

      {/* Header */}
      <section className="bg-[#111111] text-white py-16 sm:py-20 border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-[#A78E51] font-bold text-xs uppercase tracking-widest mb-3">
              <Link to="/" className="hover:underline">Forside</Link>
              <i className="fa-solid fa-angle-right text-[10px] text-neutral-500" aria-hidden="true" />
              <span>Om Andreas</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Om Andreas Guldmann
            </h1>
            <p className="mt-4 text-base sm:text-lg text-neutral-300 leading-relaxed">
              Mød tømrermesteren bag virksomheden. Her får du direkte kontakt, personligt engagement og et solidt stykke arbejde på din ejendom.
            </p>
          </div>
        </div>
      </section>

      {/* Main Story & Values */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            {/* Profile narrative */}
            <div className="space-y-7 text-neutral-600 text-base sm:text-lg leading-relaxed">
              <div>
                <span className="text-[#A78E51] font-bold text-xs uppercase tracking-widest block mb-1">
                  Virksomhedens profil
                </span>
                <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#111111] tracking-tight leading-tight">
                  Tæt dialog og stolte håndværkstraditioner
                </h2>
              </div>

              <p>
                <strong>Tømrermester Andreas Guldmann ApS</strong> er stiftet af Andreas Guldmann med base på Nyborgvej i Skårup Fyn. Virksomheden er bygget op omkring en klar målsætning: at levere tømrerarbejde i høj kvalitet, hvor kunden altid føler sig tryg og velinformeret gennem hele forløbet.
              </p>

              <p>
                Som tømrer er det glæden ved det gedigne træhåndværk, der driver værket. Vi elsker at lave døre og vinduer, og uanset om opgaven gælder en ny skræddersyet træterrasse til haven, præcis dør- og vinduesudskiftning eller indendørs renovering af lofter og vægge, er grundigheden den samme.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-12 py-5">
                <div>
                  <div className="text-[#A78E51] text-xl mb-4">
                    <i className="fa-solid fa-comments" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-sm text-[#111111] mb-1">
                    Direkte kontakt
                  </h3>
                  <p className="text-xs text-neutral-600 leading-normal">
                    Du har kontakt til Andreas direkte – fra første telefonsamtale til det afsluttende tømrerarbejde er godkendt.
                  </p>
                </div>

                <div>
                  <div className="text-[#A78E51] text-xl mb-4">
                    <i className="fa-solid fa-ruler-combined" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-sm text-[#111111] mb-1">
                    Præcision & materialer
                  </h3>
                  <p className="text-xs text-neutral-600 leading-normal">
                    Vi anvender ordentlige materialer og sunde tømrersamlinger, så konstruktionen holder til det danske klima.
                  </p>
                </div>
              </div>

              <p>
                Har du brug for en tømrer i Skårup Fyn eller det sydfynske område, er du altid velkommen til at ringe eller skrive en e-mail til Andreas for en uforpligtende snak om dit projekt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Geografisk udgangspunkt og Stamdata */}
      <section className="py-14 sm:py-20 bg-[#FAFAF8] border-t border-[#E5E5E0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="text-[#A78E51] font-bold text-xs uppercase tracking-widest block mb-2">
              Fakta & Stamdata
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#111111] tracking-tight">
              Virksomhedsoplysninger
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-lg border border-neutral-200 text-center shadow-sm">
              <i className="fa-solid fa-building text-2xl text-[#A78E51] mb-3 block" aria-hidden="true" />
              <div className="text-xs text-neutral-500 font-semibold uppercase">Virksomhed</div>
              <div className="text-sm font-bold text-[#111111] mt-1">{COMPANY.name}</div>
            </div>

            <div className="bg-white p-6 rounded-lg border border-neutral-200 text-center shadow-sm">
              <i className="fa-solid fa-location-dot text-2xl text-[#A78E51] mb-3 block" aria-hidden="true" />
              <div className="text-xs text-neutral-500 font-semibold uppercase">Adresse</div>
              <div className="text-sm font-bold text-[#111111] mt-1">{COMPANY.address}</div>
            </div>

            <div className="bg-white p-6 rounded-lg border border-neutral-200 text-center shadow-sm">
              <i className="fa-solid fa-id-card text-2xl text-[#A78E51] mb-3 block" aria-hidden="true" />
              <div className="text-xs text-neutral-500 font-semibold uppercase">CVR-nummer</div>
              <div className="text-sm font-bold text-[#111111] mt-1">{COMPANY.cvr}</div>
            </div>

            <div className="bg-white p-6 rounded-lg border border-neutral-200 text-center shadow-sm">
              <i className="fa-solid fa-phone text-2xl text-[#A78E51] mb-3 block" aria-hidden="true" />
              <div className="text-xs text-neutral-500 font-semibold uppercase">Telefon</div>
              <div className="text-sm font-bold text-[#111111] mt-1">
                <a href={COMPANY.phoneLink} className="hover:text-[#A78E51]">
                  {COMPANY.phone}
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-[#A78E51] text-white font-bold text-sm hover:bg-[#937B43] transition-colors"
            >
              <span>Kontakt Andreas Guldmann</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
